import { create } from "zustand";
import { DEFAULT_PAYMENT_CURRENCY, TxDirectionEnum } from "@/config";
import { amlbotAPI, type PaymentIntentRequest } from "@/api";
import {
  useUserStore,
  useAmlCheckStore,
  useAmlHistoryStore,
  useConfigStore,
} from "./";

export interface PaymentIntent {
  clientSecret: string | null;
  cost: number;
  currency: string;
}

export interface PaymentStore {
  paymentIntent: PaymentIntent;
  serverConfirmationStatus: "idle" | "polling" | "success" | "timeout";

  setPaymentIntent: (paymentIntent: PaymentIntent) => void;
  discardPaymentIntentByToken: (token: string) => void;
  clearPaymentIntent: () => void;
  verifyPaymentStatus: (secret: string, token: string) => Promise<boolean>;
  initPaymentIntent: () => Promise<null>;
  intentLoading: boolean;
}

export const usePaymentStore = create<PaymentStore>((set, get) => ({
  paymentIntent: {
    clientSecret: null,
    cost: 1,
    currency: DEFAULT_PAYMENT_CURRENCY,
  },
  serverConfirmationStatus: "idle",
  intentLoading: false,

  setPaymentIntent: (paymentIntent: PaymentIntent) => set({ paymentIntent }),
  clearPaymentIntent: () =>
    set({
      paymentIntent: {
        clientSecret: null,
        cost: 0,
        currency: DEFAULT_PAYMENT_CURRENCY,
      },
    }),

  initPaymentIntent: async () => {
    const { intentLoading, paymentIntent } = get();

    if (intentLoading || paymentIntent.clientSecret) {
      return null;
    }

    set({ intentLoading: true });
    const { externalData, setToken } = useAmlCheckStore.getState();
    const { setTotalCount } = useAmlHistoryStore.getState();
    const { setBalance } = useUserStore.getState();
    const { appId, externalId, username, parentOrigin, config } =
      useConfigStore.getState();

    if (!appId || !externalId || !parentOrigin) {
      set({ intentLoading: false });
      return null;
    }

    try {
      const payload: PaymentIntentRequest = {
        appId,
        parentOrigin,
        identifier: externalId,
        username: username ?? null,
        address: externalData?.address ?? "",
        network: externalData?.network ?? "",
        hash: externalData?.hash ?? "",
        direction: externalData?.direction ?? TxDirectionEnum.DEPOSIT,
        theme: config.theme,
        mode: config.widgetMode,
      };

      const response = await amlbotAPI.paymentIntent(payload);

      set({
        paymentIntent: {
          clientSecret: response.data.clientSecret,
          cost: response.data.cost,
          currency: DEFAULT_PAYMENT_CURRENCY,
        },
      });
      setToken(response.data.token);
      setBalance(response.data.user.balance);
      setTotalCount(response.data.user.totalCount);
    } catch (error) {
      console.error(error);
    } finally {
      set({ intentLoading: false });
    }

    return null;
  },

  verifyPaymentStatus: async (secret: string, token: string) => {
    set({ serverConfirmationStatus: "polling" });

    const MAX_ATTEMPTS = 10;
    const DELAY_MS = 1500;
    const setBalance = useUserStore.getState().setBalance;

    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        const response = await amlbotAPI.verifyPayment({ secret, token });

        if (response.success) {
          set({ serverConfirmationStatus: "success" });
          setBalance(Number(response.data.balance));
          return true;
        }
      } catch (error) {
        // console.warn(`Attempt ${i + 1}/${MAX_ATTEMPTS} failed. Retrying...`, error);
      }

      if (i < MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      }
    }

    set({ serverConfirmationStatus: "timeout" });
    return false;
  },
  discardPaymentIntentByToken: async (token: string) => {
    const { setToken } = useAmlCheckStore.getState();

    try {
      await amlbotAPI.discardPaymentIntent({ token });
      setToken(null);
      set({
        paymentIntent: {
          clientSecret: null,
          cost: 0,
          currency: DEFAULT_PAYMENT_CURRENCY,
        },
      });
    } catch (e) {
      //
    }
  },
}));
